import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { getDifficultyLabel } from '../utils/constants';
import {
  MessageSquare,
  Send,
  Video,
  X,
  Loader2,
  Plus,
  Utensils,
  Coffee,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Edit,
  ArrowLeft
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

// Import refactored components
import { AdminStatsCards } from '../components/admin/AdminStatsCards';
import { AdminOverviewTab } from '../components/admin/AdminOverviewTab';
import { AdminTabsWheel } from '../components/admin/AdminTabsWheel';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminPurchasesTab } from '../components/admin/AdminPurchasesTab';
import { AdminProgramsTab } from '../components/admin/AdminProgramsTab';
import { AdminWorkoutsTab } from '../components/admin/AdminWorkoutsTab';
import { AdminExerciseLibraryTab } from '../components/admin/AdminExerciseLibraryTab';
import { EditUserModal } from '../components/admin/EditUserModal';
import { SendMessageModal } from '../components/admin/SendMessageModal';
import { ViewUserProfileModal } from '../components/admin/ViewUserProfileModal';
import { BulkImportUsersModal } from '../components/admin/BulkImportUsersModal';
import { BulkImportWorkoutsModal } from '../components/admin/BulkImportWorkoutsModal';
import { BulkImportFollowsModal } from '../components/admin/BulkImportFollowsModal';
import { BulkImportChallengesModal } from '../components/BulkImportChallengesModal';
import { GenericBulkImportModal } from '../components/GenericBulkImportModal';
import { AdminDataTab } from '../components/admin/AdminDataTab';
import { AdminPreviewTab } from '../components/admin/AdminPreviewTab';
import { MessagesTab } from '../components/admin/MessagesTab';
import { AdminUserPlansTab } from '../components/admin/AdminUserPlansTab';
import { AdminNutritionPlansTab } from '../components/admin/AdminNutritionPlansTab';
import { MealPlansTab } from '../components/admin/MealPlansTab';
import { downloadJson } from '../utils/downloadJson';

const LOGO_URL = '/logo.webp';
const API = process.env.REACT_APP_BACKEND_URL;


// Main Admin Dashboard Component
export const AdminDashboardPage = () => {
  const { user, api, token } = useAuth();
  const navigate = useNavigate();
  const { section } = useParams();
  const location = useLocation();
  // The active tab is now derived directly from the URL — visiting
  // `/admin/users` shows the Users section, `/admin` (no section) shows
  // the Overview. Setter is a small helper that pushes a new route so
  // browser back/forward integrate cleanly.
  const activeTab = section || 'overview';
  const setActiveTab = (id) => navigate(id === 'overview' ? '/admin' : `/admin/${id}`);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [registrationData, setRegistrationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
  const usersPerPage = 50;
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [bulkImportWorkoutsOpen, setBulkImportWorkoutsOpen] = useState(false);
  const [bulkImportFollowsOpen, setBulkImportFollowsOpen] = useState(false);
  const [bulkImportChallengesOpen, setBulkImportChallengesOpen] = useState(false);
  const [bulkImportParticipantsOpen, setBulkImportParticipantsOpen] = useState(false);
  
  // Edit User Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  
  // Message Modal State
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messagingUser, setMessagingUser] = useState(null);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [messageVideoUrl, setMessageVideoUrl] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // View Profile Modal State
  const [viewProfileModalOpen, setViewProfileModalOpen] = useState(false);
  const [viewingUserId, setViewingUserId] = useState(null);

  const openViewProfileModal = (user) => {
    setViewingUserId(user.id);
    setViewProfileModalOpen(true);
  };

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  // Deep-link: /admin/users?viewUser=<id> opens the user profile modal
  // directly (used by the "View in Admin" button on new-user notifications).
  useEffect(() => {
    if (!user?.is_admin) return;
    const params = new URLSearchParams(location.search);
    const viewUser = params.get('viewUser');
    if (viewUser) {
      setViewingUserId(viewUser);
      setViewProfileModalOpen(true);
      // Clean the query string so it doesn't re-trigger on back/forward
      navigate(location.pathname, { replace: true });
    }
  }, [user, location.search, location.pathname, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        api().get('/admin/stats'),
        api().get(`/admin/users?limit=${usersPerPage}&skip=0`),
        api().get('/admin/purchases?limit=100'),
        api().get('/admin/programs'),
        api().get('/admin/registrations?days=30'),
        api().get('/admin/workouts')
      ]);
      
      if (results[0].status === 'fulfilled') setStats(results[0].value.data);
      if (results[1].status === 'fulfilled') {
        setUsers(results[1].value.data.users);
        setTotalUsers(results[1].value.data.total);
      }
      if (results[2].status === 'fulfilled') setPurchases(results[2].value.data.purchases || []);
      if (results[3].status === 'fulfilled') setPrograms(results[3].value.data.programs || []);
      if (results[4].status === 'fulfilled') setRegistrationData(results[4].value.data.registrations || []);
      if (results[5].status === 'fulfilled') setWorkouts(results[5].value.data.workouts || []);
      
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length === results.length) {
        toast.error('Failed to load admin data. Please check your connection.');
      } else if (failed.length > 0) {
        toast.error(`Some admin data failed to load (${failed.length} of ${results.length})`);
      }
    } catch {
      toast.error('Failed to load admin data');
    }
    setLoading(false);
  };

  const loadMoreUsers = async () => {
    setLoadingMoreUsers(true);
    try {
      const nextPage = usersPage + 1;
      const res = await api().get(`/admin/users?limit=${usersPerPage}&skip=${nextPage * usersPerPage}`);
      setUsers(prev => [...prev, ...res.data.users]);
      setUsersPage(nextPage);
    } catch (err) {
      toast.error('Failed to load more users');
    }
    setLoadingMoreUsers(false);
  };

  const loadAllUsers = async () => {
    setLoadingMoreUsers(true);
    try {
      const res = await api().get(`/admin/users?limit=1000&skip=0`);
      setUsers(res.data.users);
      setTotalUsers(res.data.total);
      setUsersPage(Math.ceil(res.data.total / usersPerPage));
      toast.success(`Loaded all ${res.data.total} members`);
    } catch (err) {
      toast.error('Failed to load all users');
    }
    setLoadingMoreUsers(false);
  };

  const exportCollection = async (endpoint, key, filenameBase) => {
    try {
      const res = await api().get(endpoint);
      const items = res.data[key] || [];
      if (!items.length) {
        toast.error(`No ${key} to export`);
        return;
      }
      downloadJson(items, filenameBase);
      toast.success(`Exported ${items.length} ${key}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to export ${key}`);
    }
  };

  const openEditModal = (userToEdit) => {
    setEditingUser({ ...userToEdit });
    setEditModalOpen(true);
  };

  const saveUserEdits = async () => {
    if (!editingUser) return;
    setSavingUser(true);
    try {
      const res = await api().put(`/admin/users/${editingUser.id}`, editingUser);
      setUsers(prev => prev.map(u => u.id === editingUser.id ? res.data.user : u));
      toast.success('User updated successfully');
      setEditModalOpen(false);
      setEditingUser(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
    setSavingUser(false);
  };

  const openMessageModal = (userToMessage) => {
    setMessagingUser(userToMessage);
    setMessageSubject('');
    setMessageContent('');
    setMessageVideoUrl('');
    setMessageModalOpen(true);
  };

  const sendMessage = async () => {
    if (!messagingUser || !messageSubject.trim() || !messageContent.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    setSendingMessage(true);
    try {
      await api().post('/admin/messages', {
        user_id: messagingUser.id,
        subject: messageSubject,
        content: messageContent,
        video_url: messageVideoUrl || null
      });
      toast.success(`Message sent to ${messagingUser.name}`);
      setMessageModalOpen(false);
      setMessagingUser(null);
    } catch (err) {
      toast.error('Failed to send message');
    }
    setSendingMessage(false);
  };

  const togglePremium = async (userToToggle) => {
    const currentlyPremium = !!userToToggle.is_premium;
    const action = currentlyPremium ? 'revoke' : 'grant';
    const endpoint = currentlyPremium ? 'revoke-premium' : 'grant-premium';
    if (currentlyPremium && !window.confirm(`Revoke Premium from ${userToToggle.email}?`)) return;
    try {
      const res = await api().post(`/admin/users/${userToToggle.id}/${endpoint}`);
      setUsers(prev => prev.map(u => u.id === userToToggle.id
        ? {
            ...u,
            is_premium: !currentlyPremium,
            premium_plan: currentlyPremium ? null : 'admin_grant',
            premium_expires_at: currentlyPremium ? null : res.data.expires_at,
          }
        : u
      ));
      toast.success(currentlyPremium
        ? `Premium revoked from ${userToToggle.email}`
        : `Premium granted to ${userToToggle.email}`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} premium`);
    }
  };

  const deleteProgram = async (programId) => {
    if (!confirm('Are you sure you want to delete this program?')) return;
    try {
      await api().delete(`/admin/programs/${programId}`);
      toast.success('Program deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete program');
    }
  };

  const duplicateProgram = async (programId) => {
    try {
      const res = await api().post(`/admin/programs/${programId}/duplicate`);
      toast.success(`Created: ${res.data.new_program.title}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to duplicate program');
    }
  };

  const duplicateWorkout = async (workoutId) => {
    try {
      const res = await api().post(`/admin/workouts/${workoutId}/duplicate`);
      toast.success(`Created: ${res.data.new_workout.title}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to duplicate workout');
    }
  };

  const deleteWorkout = async (workoutId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api().delete(`/admin/workouts/${workoutId}`);
      toast.success(`Deleted: ${title}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete workout');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <img src={LOGO_URL} alt="Ascend & Surpass" className="h-10 w-10" />
            </Link>
            <div>
              <h1 className="font-heading text-2xl text-white uppercase tracking-wider">Admin Dashboard</h1>
              <p className="text-muted-foreground text-sm">Manage your fitness empire</p>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Cards */}
        <AdminStatsCards stats={stats} onTabChange={setActiveTab} />

        {/* Tabs — spinnable wheel, consistent with the user-facing quick
            links. Triggered via collapsible pill to keep the admin
            dashboard's "above the fold" space clean. */}
        <AdminTabsWheel activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <AdminOverviewTab stats={stats} registrationData={registrationData} />
        )}

        {activeTab === 'users' && (
          <AdminUsersTab
            users={users}
            totalUsers={totalUsers}
            loadingMoreUsers={loadingMoreUsers}
            onLoadMoreUsers={loadMoreUsers}
            onLoadAllUsers={loadAllUsers}
            onEditUser={openEditModal}
            onMessageUser={openMessageModal}
            onViewProfile={openViewProfileModal}
            onTogglePremium={togglePremium}
            onBulkImport={() => setBulkImportOpen(true)}
            onBulkImportFollows={() => setBulkImportFollowsOpen(true)}
            onExportUsers={() => exportCollection('/admin/users/export', 'users', 'users-backup')}
            onExportFollows={() => exportCollection('/admin/follows/export', 'follows', 'follows-backup')}
          />
        )}

        {activeTab === 'data' && (
          <AdminDataTab
            api={api}
            onBulkImportUsers={() => setBulkImportOpen(true)}
            onBulkImportWorkouts={() => setBulkImportWorkoutsOpen(true)}
            onBulkImportFollows={() => setBulkImportFollowsOpen(true)}
            onBulkImportChallenges={() => setBulkImportChallengesOpen(true)}
            onBulkImportChallengeParticipants={() => setBulkImportParticipantsOpen(true)}
          />
        )}

        {activeTab === 'preview' && <AdminPreviewTab />}

        {activeTab === 'purchases' && (
          <AdminPurchasesTab purchases={purchases} />
        )}

        {activeTab === 'programs' && (
          <AdminProgramsTab
            programs={programs}
            onDuplicate={duplicateProgram}
            onDelete={deleteProgram}
          />
        )}

        {activeTab === 'workouts' && (
          <AdminWorkoutsTab
            workouts={workouts}
            onDuplicate={duplicateWorkout}
            onDelete={deleteWorkout}
            users={users}
            onBulkImport={() => setBulkImportWorkoutsOpen(true)}
            onExport={() => exportCollection('/admin/workouts/export', 'workouts', 'workouts-backup')}
          />
        )}

        {activeTab === 'exercises' && (
          <AdminExerciseLibraryTab api={api} />
        )}

        {activeTab === 'messages' && (
          <MessagesTab api={api} token={token} />
        )}

        {activeTab === 'meal-plans' && (
          <MealPlansTab api={api} token={token} />
        )}

        {activeTab === 'user-plans' && (
          <AdminUserPlansTab api={api} users={users} programs={programs} workouts={workouts} />
        )}

        {activeTab === 'nutrition' && (
          <AdminNutritionPlansTab api={api} users={users} programs={programs} />
        )}

      </div>

      {/* Modals */}
      <EditUserModal
        isOpen={editModalOpen}
        user={editingUser}
        onClose={() => setEditModalOpen(false)}
        onSave={saveUserEdits}
        saving={savingUser}
        onChange={setEditingUser}
      />

      <SendMessageModal
        isOpen={messageModalOpen}
        user={messagingUser}
        onClose={() => setMessageModalOpen(false)}
        subject={messageSubject}
        onSubjectChange={setMessageSubject}
        content={messageContent}
        onContentChange={setMessageContent}
        videoUrl={messageVideoUrl}
        onVideoUrlChange={setMessageVideoUrl}
        onSend={sendMessage}
        sending={sendingMessage}
        token={token}
      />

      {/* View User Profile Modal */}
      <ViewUserProfileModal
        isOpen={viewProfileModalOpen}
        onClose={() => setViewProfileModalOpen(false)}
        userId={viewingUserId}
        api={api}
      />

      <BulkImportUsersModal
        isOpen={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        api={api}
        onImportComplete={loadAllUsers}
      />

      <BulkImportWorkoutsModal
        isOpen={bulkImportWorkoutsOpen}
        onClose={() => setBulkImportWorkoutsOpen(false)}
        api={api}
        onImportComplete={fetchData}
      />

      <BulkImportFollowsModal
        isOpen={bulkImportFollowsOpen}
        onClose={() => setBulkImportFollowsOpen(false)}
        api={api}
        onImportComplete={() => {}}
      />

      <BulkImportChallengesModal
        isOpen={bulkImportChallengesOpen}
        onClose={() => setBulkImportChallengesOpen(false)}
        api={api}
        onImportComplete={() => {}}
      />

      <GenericBulkImportModal
        isOpen={bulkImportParticipantsOpen}
        onClose={() => setBulkImportParticipantsOpen(false)}
        api={api}
        title="Bulk Import Challenge Participants"
        description="Paste participant records as JSON. Use either UUIDs or emails/titles. Duplicates on (challenge, user) are skipped."
        endpoint="/admin/challenge-participants/bulk-import"
        payloadKey="participants"
        totalKey="total_participants_in_db"
        testId="bulk-import-participants-modal"
        placeholder={`[\n  {\n    "challenge_title": "30-Day Workout Warrior",\n    "user_email": "jane@example.com",\n    "current_value": 12,\n    "completed": false,\n    "joined_at": "2026-04-05T13:07:56+00:00"\n  }\n]`}
      />
    </div>
  );
};
